import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Share as ShareIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Pinterest as PinterestIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { SocialService } from "../../services/social-service";

interface ShareMenuProps {
  outfitId: string;
  size?: "small" | "medium" | "large";
}

/**
 * Share menu component for outfits
 */
const ShareMenu: React.FC<ShareMenuProps> = ({ outfitId, size = "medium" }) => {
  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle share to platform
  const handleShareToPlatform = (
    platform: "twitter" | "facebook" | "pinterest"
  ) => {
    const shareUrl = SocialService.getShareUrl(outfitId, platform);
    window.open(shareUrl, "_blank", "noopener,noreferrer");
    handleMenuClose();
  };

  // Handle copy link
  const handleCopyLink = () => {
    const outfitUrl = `${window.location.origin}/outfit/${outfitId}`;
    navigator.clipboard.writeText(outfitUrl);

    setSnackbarMessage("Link copied to clipboard");
    setSnackbarOpen(true);
    handleMenuClose();
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Tooltip title="Share">
        <IconButton
          onClick={handleMenuOpen}
          size={size}
          aria-label="Share"
          aria-controls="share-menu"
          aria-haspopup="true"
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="share-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleShareToPlatform("twitter")}>
          <ListItemIcon>
            <TwitterIcon fontSize="small" style={{ color: "#1DA1F2" }} />
          </ListItemIcon>
          <ListItemText>Twitter</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleShareToPlatform("facebook")}>
          <ListItemIcon>
            <FacebookIcon fontSize="small" style={{ color: "#4267B2" }} />
          </ListItemIcon>
          <ListItemText>Facebook</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleShareToPlatform("pinterest")}>
          <ListItemIcon>
            <PinterestIcon fontSize="small" style={{ color: "#E60023" }} />
          </ListItemIcon>
          <ListItemText>Pinterest</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareMenu;
